import type { FrequencyIssue, FrequencyWarning } from '@wheere/api-site';

/**
 * Refus 422 `FrequencyFileRejection` (ADR-039) lu depuis une erreur HTTP : toutes les erreurs,
 * chiffrées et localisées par le serveur. Le front affiche les `message` tels quels ; les codes ne
 * servent qu'à la présentation (renvoi vers le Convertisseur, badges).
 */
export interface FrequencyRejectionView {
  /** `FREQUENCY_FILE_REJECTED`, `CALIB_REJECTED`, `PRESET_REJECTED`, `LEGACY_CONVERSION_REJECTED`,
   * `FREQUENCY_SETUP_INVALID`, ou null si le corps n'en porte pas. */
  code: string | null;
  message: string;
  issues: FrequencyIssue[];
  warnings: FrequencyWarning[];
  /** Ancienne grille .txt (freq ou theta) : à convertir dans Outils › Convertisseur. */
  legacyFormat: boolean;
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : null;
}

function hasCodeAndMessage(v: unknown): boolean {
  const r = asRecord(v);
  return !!r && typeof r['code'] === 'string' && typeof r['message'] === 'string';
}
const isIssue = (v: unknown): v is FrequencyIssue => hasCodeAndMessage(v);
const isWarning = (v: unknown): v is FrequencyWarning => hasCodeAndMessage(v);

/**
 * Refus 422 au format `FrequencyFileRejection`, sinon null (autre statut, corps d'une autre forme :
 * l'appelant retombe sur le message générique `errorMessage`).
 */
export function frequencyRejection(e: unknown): FrequencyRejectionView | null {
  const http = asRecord(e);
  if (!http || http['status'] !== 422) return null;
  const body = asRecord(http['error']);
  if (!body || !Array.isArray(body['issues'])) return null;
  const issues = (body['issues'] as unknown[]).filter(isIssue);
  const warnings = Array.isArray(body['warnings']) ? (body['warnings'] as unknown[]).filter(isWarning) : [];
  return {
    code: typeof body['code'] === 'string' ? body['code'] : null,
    message: typeof body['message'] === 'string' ? body['message'] : '',
    issues,
    warnings,
    legacyFormat: issues.some(i => i.code === 'FREQ_FILE_LEGACY_FORMAT'),
  };
}

/** Lignes à afficher : un motif par issue (message serveur), ou le message global à défaut. */
export function rejectionLines(r: FrequencyRejectionView): string[] {
  if (r.issues.length) return r.issues.map(i => i.message);
  return r.message ? [r.message] : [];
}
